import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { getRequest, postRequest } from '../../../../../Helper/ApiClient';
import type { BoothPanelProps } from '../panels';
import type { IVtsExhibitorMediaFile } from './types';
import { formatMegabytes } from './formatBytes';
import MediaRow from './MediaRow/MediaRow';
import styles from './MediaPanel.module.scss?module';

interface MediaGroup {
  name: string;
  files: IVtsExhibitorMediaFile[];
}

const SKELETON_ROWS = 4;
const UNCATEGORISED = 'Other';

/**
 * Media & downloads: the complete media kit as one ZIP and the single files, grouped by
 * category in the order the API delivers them.
 */
export default function MediaPanel({
  exhibitor,
  tab,
  onBadge,
  track,
}: BoothPanelProps) {
  const filesRequest = getRequest<IVtsExhibitorMediaFile[]>(
    '/api/virtual-trade-show/exhibitor-media/' + exhibitor.id
  );
  const { mutate: postKitDownload } = postRequest(
    '/api/virtual-trade-show/download-media-kit'
  );

  const files = Array.isArray(filesRequest.data) ? filesRequest.data : [];
  const badge = filesRequest.isSuccess ? files.length : null;
  const kitUrl = exhibitor.complete_media_kit_url;
  const hasKit = exhibitor.complete_media_kit_status === 'ready' && !!kitUrl;

  useEffect(() => {
    onBadge(tab, badge);
  }, [tab, onBadge, badge]);

  const handleKitDownload = () => {
    const formData = new FormData();
    formData.append('exhibitor_id', String(exhibitor.id));
    postKitDownload(formData);
    window.open(kitUrl, '_blank', 'noopener');
    track('media_downloaded', { kind: 'zip' });
  };

  const handleFileDownload = (file: IVtsExhibitorMediaFile) => {
    track('media_downloaded', { kind: 'file', name: file.name });
  };

  return (
    <div className={styles.panel}>
      {hasKit && (
        <section className={styles.kitCard}>
          <div className={styles.kitText}>
            <h2 className={styles.sectionLabel}>Complete media kit</h2>
            <p className={styles.kitDescription}>
              All media files of {exhibitor.name} in one ZIP archive.
            </p>
          </div>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleKitDownload}
          >
            <FontAwesomeIcon icon={faDownload} />
            <span>
              Download complete media kit{' '}
              <span className={styles.noWrap}>
                (ZIP · {formatMegabytes(exhibitor.complete_media_kit_byte_size)}
                )
              </span>
            </span>
          </button>
        </section>
      )}

      {filesRequest.isPending ? (
        <ul className={styles.fileList} aria-label="Loading media files">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <li key={index} className={styles.skeletonRow} />
          ))}
        </ul>
      ) : filesRequest.isError ? (
        <p className={styles.message} role="alert">
          Media files could not be loaded. Please try again later.
        </p>
      ) : files.length === 0 ? (
        <p className={styles.message}>No media files yet.</p>
      ) : (
        groupByCategory(files).map((group) => (
          <section key={group.name} className={styles.section}>
            <h2 className={styles.sectionLabel}>{group.name}</h2>
            <ul className={styles.fileList}>
              {group.files.map((file) => (
                <MediaRow
                  key={file.id}
                  file={file}
                  onDownload={handleFileDownload}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

/** Groups the files by category; groups keep the order of their first file. */
function groupByCategory(files: IVtsExhibitorMediaFile[]): MediaGroup[] {
  const groups: MediaGroup[] = [];
  const byName = new Map<string, MediaGroup>();
  files.forEach((file) => {
    const name = file.category_name?.trim() || UNCATEGORISED;
    let group = byName.get(name);
    if (!group) {
      group = { name, files: [] };
      byName.set(name, group);
      groups.push(group);
    }
    group.files.push(file);
  });

  return groups;
}
